import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  addCaseDefendantLink,
  editCaseDefendantLink,
} from "services/caseServices";
import { editSuspect2 } from "services/lealIntityService";
import {
  CaseCreation,
  CaseDefendantLink,
  Reporter,
  Suspect,
  Victim,
  Witness,
} from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandler, errorHandlerOnForm } from "utils/helper";

type Props = {
  caseData: CaseCreation;
  action: "add" | "edit";
  selectedData?: CaseDefendantLink;
  caseRelatedParties: {
    suspects: Suspect[];
    witnesses: Witness[];
    victims: Victim[];
    reporters: Reporter[];
  };
  onClose: (refetch: boolean) => void;
};

const SuspectClosureForm: FC<Props> = ({
  caseData,
  action,
  selectedData,
  caseRelatedParties,
  onClose,
}) => {
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const [suspect, setSuspect] = useState<string>();
  const [reasonForClosure, setReasonForClosure] = useState<string>();

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  useEffect(() => {
    if (selectedData) {
      setSuspect(
        selectedData?.defendant_info?.uu_id
          ? String(selectedData.defendant_info.uu_id)
          : "",
      );
      // setReasonForClosure(
      //   selectedData?.reason_for_closure !== undefined
      //     ? String(selectedData.defendant_number)
      //     : "",
      // );
    }
  }, [selectedData]);

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="bg-white rounded-lg shadow-lg p-6 w-[600px]">
        <h2 className="text-lg font-semibold text-gray-800">Add Defendants</h2>

        <div className="flex flex-col mt-4">
          <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
            Select Defendants
          </label>
          <select
            className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
            value={suspect}
            onChange={(e) => {
              setSuspect(e.target.value);
            }}
            disabled={action === "edit"}
          >
            <option value="">Select Defendant</option>
            {caseRelatedParties?.suspects
              ?.filter(
                (a) =>
                  !caseData?.case_defendant_links?.find(
                    (d) => d?.defendant_info?.uu_id === a?.uu_id,
                  ) && a?.closed_by_42 !== true,
              )
              .map((f) => (
                <option key={f?.uu_id} value={f?.uu_id}>
                  {f.first_name} {f.middle_name} {f.last_name}
                </option>
              ))}
          </select>
        </div>
        <div className="flex flex-col mt-4 w-full">
          <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
            Reason for Closure
          </label>
          <textarea
            className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
            placeholder="Enter Reason for Closure"
            rows={4}
            value={reasonForClosure}
            onChange={(e) => setReasonForClosure(e.target.value)}
          />
        </div>

        <div className="mt-4 flex justify-between gap-2">
          <button
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded w-full hover:bg-gray-300"
            onClick={() => {
              onClose(false);
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`border border-[#E90101] bg-[#E90101] px-10 py-2 text-white 
                                  font-[Poppins] cursor-pointer font-[500] w-full
                                  text-base rounded-md hover:bg-white hover:text-[#E90101]
                                  ${
                                    loading
                                      ? "opacity-50 cursor-not-allowed"
                                      : ""
                                  }`}
            disabled={loading}
            onClick={() => {
              setLoading(true);
              if (suspect === undefined || suspect === "") {
                setLoading(false);
                showTooltip(t("defendant_is_required"), "error");
                return;
              }
              if (reasonForClosure === undefined || reasonForClosure === "") {
                setLoading(false);
                showTooltip(t("reason_for_closure_is_required"), "error");
                return;
              }

              editSuspect2({
                uu_id: suspect,
                reason_for_closure: reasonForClosure,
                is_defendant: false,
                closed_by_42: true,
              })
                .then(() => {
                  setLoading(false);
                  showTooltip(t("operation_completed_successfully"), "success");
                  setReasonForClosure("");
                  setSuspect("");
                  onClose(true);
                })
                .catch((error) => {
                  setLoading(false);
                  showTooltip(
                    error?.response?.data?.detail ||
                      errorHandlerOnForm(error) ||
                      errorHandler(error) ||
                      "Something went wrong. Please try again.",
                    "error",
                  );
                });
            }}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                {LoadingIcon}
                Loading...
              </div>
            ) : action === "add" ? (
              "Add"
            ) : (
              "Edit"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SuspectClosureForm;
